import React, { useState } from 'react';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/usePermissions';
import { pimService } from '../../services/pimService';
import { useNotification } from '../../contexts/NotificationContext';
import { API_CONFIG } from '../../config/api';

interface ImportResult {
  success: boolean;
  message: string;
  totalRows?: number;
  successfulRows?: number;
  failedRows?: number;
  errors?: Array<{ row: number; field: string; message: string }>;
}

interface ExportOptions {
  format: 'csv' | 'json';
  includeAttributes: boolean;
  includeMedia: boolean;
  includeCategories: boolean;
  includeVariants: boolean;
  productTypeId?: number;
  categoryId?: number;
}

export const ImportExport: React.FC = () => {
  const { t } = useTranslation();
  const { canManagePim, canWriteProducts, canReadProducts } = usePermissions();
  const { addNotification } = useNotification();
  
  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'csv',
    includeAttributes: true,
    includeMedia: false,
    includeCategories: true,
    includeVariants: false
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.type === 'application/json' || file.name.endsWith('.csv') || file.name.endsWith('.json')) {
        setImportFile(file);
        setImportResult(null);
      } else {
        addNotification('error', t('pim.importExport.messages.invalidFile'));
      }
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await fetch('/api/v1/pim/products/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        setImportResult(result);
        addNotification('success', t('pim.importExport.messages.importComplete', { success: result.successfulRows, total: result.totalRows }));
      } else {
        throw new Error(result.message || t('pim.importExport.messages.importFailed'));
      }
    } catch (error) {
      addNotification('error', `${t('pim.importExport.messages.importFailed')}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams({
        format: exportOptions.format,
        includeAttributes: exportOptions.includeAttributes.toString(),
        includeMedia: exportOptions.includeMedia.toString(),
        includeCategories: exportOptions.includeCategories.toString(),
        includeVariants: exportOptions.includeVariants.toString()
      });

      if (exportOptions.productTypeId) {
        params.append('productTypeId', exportOptions.productTypeId.toString());
      }
      if (exportOptions.categoryId) {
        params.append('categoryId', exportOptions.categoryId.toString());
      }

      const response = await fetch(`${API_CONFIG.baseURL}/pim/products/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `products_export_${new Date().toISOString().split('T')[0]}.${exportOptions.format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        addNotification('success', t('pim.importExport.messages.exportComplete'));
      } else {
        const result = await response.json();
        throw new Error(result.message || t('pim.importExport.messages.exportFailed'));
      }
    } catch (error) {
      addNotification('error', `${t('pim.importExport.messages.exportFailed')}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setExportLoading(false);
    }
  };

  const downloadTemplate = (format: 'csv' | 'json') => {
    const templates = {
      csv: `name,sku,description,status,productTypeId,categoryIds
"Przykładowy Produkt 1","PROD-001","Szczegółowy opis produktu",draft,1,"1,2,3"
"Przykładowy Produkt 2","PROD-002","Inny opis produktu",published,1,"1"
"Przykładowy Produkt 3","PROD-003","Kolejny przykład",draft,2,"2,3"`,
      json: JSON.stringify([
        {
          name: "Przykładowy Produkt 1",
          sku: "PROD-001",
          description: "Szczegółowy opis produktu nr 1",
          status: "draft",
          productTypeId: 1,
          categoryIds: [1, 2, 3],
          attributes: {
            kolor: "Czerwony",
            rozmiar: "Medium",
            waga: "1.5kg"
          }
        },
        {
          name: "Przykładowy Produkt 2",
          sku: "PROD-002",
          description: "Szczegółowy opis produktu nr 2",
          status: "published",
          productTypeId: 1,
          categoryIds: [1],
          attributes: {
            kolor: "Niebieski",
            rozmiar: "Large"
          }
        },
        {
          name: "Przykładowy Produkt 3",
          sku: "PROD-003",
          description: "Szczegółowy opis produktu nr 3",
          status: "draft",
          productTypeId: 2,
          categoryIds: [2, 3],
          attributes: {
            material: "Bawełna",
            producent: "ABC Company"
          }
        }
      ], null, 2)
    };

    const content = templates[format];
    const blob = new Blob([content], {
      type: format === 'csv' ? 'text/csv;charset=utf-8;' : 'application/json;charset=utf-8;'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `szablon_import_produktow.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (!canReadProducts) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.importExport.accessDenied')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('pim.importExport.accessDeniedDesc')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('pim.importExport.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('pim.importExport.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('import')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'import'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ArrowUpTrayIcon className="h-5 w-5 inline-block mr-2" />
            {t('pim.importExport.import')}
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'export'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ArrowDownTrayIcon className="h-5 w-5 inline-block mr-2" />
            {t('pim.importExport.export')}
          </button>
        </nav>
      </div>

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* Templates */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">{t('pim.importExport.downloadTemplates')}</h3>
            <div className="flex space-x-4">
              <button
                onClick={() => downloadTemplate('csv')}
                className="flex items-center px-3 py-2 text-sm bg-white border border-blue-300 rounded-md hover:bg-blue-50"
              >
                <TableCellsIcon className="h-4 w-4 mr-2" />
                {t('pim.importExport.csvTemplate')}
              </button>
              <button
                onClick={() => downloadTemplate('json')}
                className="flex items-center px-3 py-2 text-sm bg-white border border-blue-300 rounded-md hover:bg-blue-50"
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                {t('pim.importExport.jsonTemplate')}
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('pim.importExport.uploadFile')}</h3>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      {importFile ? importFile.name : t('pim.importExport.chooseFile')}
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept=".csv,.json"
                      className="sr-only"
                      onChange={handleFileSelect}
                    />
                    <span className="mt-1 block text-sm text-gray-500">
                      {t('pim.importExport.supportedFormats')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {importFile && (
              <div className="mt-4 flex justify-between items-center">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{importFile.name}</span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({(importFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                {canWriteProducts && (
                  <button
                    onClick={handleImport}
                    disabled={importLoading}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importLoading ? (
                      <>
                        <ClockIcon className="h-4 w-4 mr-2 inline-block animate-spin" />
                        {t('pim.importExport.importing')}
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon className="h-4 w-4 mr-2 inline-block" />
                        {t('pim.importExport.importProducts')}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Import Results */}
          {importResult && (
            <div className={`border rounded-lg p-6 ${
              importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start">
                {importResult.success ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-400 mt-0.5 mr-3" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mt-0.5 mr-3" />
                )}
                <div className="flex-1">
                  <h3 className={`text-lg font-medium ${
                    importResult.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {t('pim.importExport.import')} {importResult.success ? t('pim.importExport.importResult.completed') : t('pim.importExport.importResult.failed')}
                  </h3>
                  <p className={`mt-1 text-sm ${
                    importResult.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {importResult.message}
                  </p>
                  
                  {importResult.totalRows && (
                    <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium">{t('pim.importExport.importResult.total')}</span> {importResult.totalRows}
                      </div>
                      <div>
                        <span className="font-medium">{t('pim.importExport.importResult.success')}</span> {importResult.successfulRows || 0}
                      </div>
                      <div>
                        <span className="font-medium">{t('pim.importExport.importResult.failed')}</span> {importResult.failedRows || 0}
                      </div>
                    </div>
                  )}

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-red-900 mb-2">{t('pim.importExport.importResult.errors')}</h4>
                      <div className="max-h-40 overflow-y-auto">
                        {importResult.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            Row {error.row}, {error.field}: {error.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('pim.importExport.exportOptions')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pim.importExport.exportFormat')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="csv"
                      checked={exportOptions.format === 'csv'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value as 'csv' | 'json' }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('pim.importExport.csvFormat')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="json"
                      checked={exportOptions.format === 'json'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value as 'csv' | 'json' }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('pim.importExport.jsonFormat')}</span>
                  </label>
                </div>
              </div>

              {/* Include Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pim.importExport.includeData')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeAttributes}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeAttributes: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('pim.importExport.productAttributes')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeCategories}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeCategories: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('pim.importExport.categories')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeMedia}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeMedia: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('pim.importExport.mediaUrls')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeVariants}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeVariants: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('pim.importExport.productVariants')}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? (
                  <>
                    <ClockIcon className="h-4 w-4 mr-2 inline-block animate-spin" />
                    {t('pim.importExport.exporting')}
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2 inline-block" />
                    {t('pim.importExport.exportProducts')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Export Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">{t('pim.importExport.exportInfo')}</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {t('pim.importExport.exportInfoList.csv')}</li>
              <li>• {t('pim.importExport.exportInfoList.json')}</li>
              <li>• {t('pim.importExport.exportInfoList.processing')}</li>
              <li>• {t('pim.importExport.exportInfoList.download')}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportExport;